import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ShareModal from '../Modals/ShareModal';
import { notifyMemorialFollowers } from '../../hooks/useTributeNotifications';
import { useAuth } from '../../context/AuthContext';

const { FiHeart, FiMaximize2, FiPlus, FiSmile, FiCamera, FiZap, FiShare2, FiX, FiCheck, FiFeather } = FiIcons;

const AddMemoryModal = ({ isOpen, onClose, onSubmit, memorial }) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setIsSubmitting(true);

    const authorName = user?.name || user?.email || 'A Community Member';
    const newMemory = {
      id: Date.now(), type: 'memory',
      title: 'A Shared Memory', text,
      author: authorName, likes: 0, liked: false
    };

    onSubmit(newMemory);

    // ✅ Notify memorial followers about new memory/showcase contribution
    if (memorial?.id) {
      await notifyMemorialFollowers({
        memorialPageId: memorial.id,
        memorialName: memorial.name,
        actorName: authorName,
        tributeType: 'tribute',
        giftType: null,
      });
      window.dispatchEvent(new CustomEvent('memorial_activity', {
        detail: { type: 'memory', author: authorName, memorialId: memorial.id, timestamp: new Date().toISOString() }
      }));
    }

    setText('');
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden border border-brand-dark"
      >
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Share a Memory</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Add to the Memory Wall</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <textarea required
            className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none h-40 resize-none text-sm leading-relaxed"
            placeholder="Share a story, a quote, or a beautiful moment you remember..."
            value={text} onChange={e => setText(e.target.value)}
          />
          <button type="submit" disabled={isSubmitting}
            className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-3 shadow-xl"
          >
            {isSubmitting ? (
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : <><SafeIcon icon={FiCheck} /> Post Memory</>}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const ShowcaseCard = ({ item, index, onLike, onShare }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      className={`relative break-inside-avoid mb-6 rounded-3xl overflow-hidden border border-brand-dark group cursor-pointer bg-white transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${item.type === 'quote' ? 'p-8 md:p-10' : ''}`}
    >
      {(item.type === 'photo' || item.type === 'gif') && (
        <div className="relative">
          <img src={item.url} alt="Memory" className="w-full h-auto object-cover" />
          {item.type === 'gif' && (
            <div className="absolute top-4 left-4 bg-accent-gold text-white px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiZap} className="w-2.5 h-2.5" /> Animated
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
            <div className="flex items-center gap-2 text-white text-xs font-bold">
              <div className="w-6 h-6 rounded-full bg-accent-gold flex items-center justify-center text-[10px]">{item.author[0]}</div>
              {item.author}
            </div>
            <div className="flex gap-2">
              <button onClick={(e) => { e.stopPropagation(); onShare(item); }} className="p-2 bg-white/20 backdrop-blur-md rounded-lg text-white hover:bg-white/40 transition-colors">
                <SafeIcon icon={FiShare2} className="w-4 h-4" />
              </button>
              <button className="p-2 bg-white/20 backdrop-blur-md rounded-lg text-white hover:bg-white/40 transition-colors">
                <SafeIcon icon={FiMaximize2} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {item.type === 'quote' && (
        <div className="flex flex-col h-full">
          <div className="mb-6">
            <SafeIcon icon={FiFeather} className="text-accent-gold w-8 h-8 opacity-40 mb-4" />
            <p className="text-xl md:text-2xl font-serif italic text-accent-navy leading-relaxed">"{item.text}"</p>
          </div>
          <div className="mt-auto flex items-center justify-between pt-6 border-t border-brand-light">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-light flex items-center justify-center text-accent-navy font-bold text-[10px]">{item.author[0]}</div>
              <span className="text-xs font-bold text-text-muted">{item.author}</span>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onShare(item); }} className="p-2 text-text-muted hover:text-accent-gold transition-colors">
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {item.type === 'memory' && (
        <div className="p-6">
          {item.url && (
            <div className="rounded-2xl overflow-hidden mb-4 relative">
              <img src={item.url} alt="Memory" className="w-full h-48 object-cover" />
            </div>
          )}
          <h4 className="font-bold text-accent-navy mb-2">{item.title}</h4>
          <p className="text-sm text-text-muted font-light leading-relaxed line-clamp-4 mb-4">{item.text}</p>
          <div className="flex items-center justify-between pt-4 border-t border-brand-light">
            <span className="text-[10px] font-black uppercase tracking-widest text-accent-gold">{item.author}</span>
            <div className="flex gap-3 text-text-muted">
              <button onClick={(e) => { e.stopPropagation(); onShare(item); }} className="flex items-center gap-1 hover:text-accent-gold transition-colors">
                <SafeIcon icon={FiShare2} className="w-3.5 h-3.5" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); onLike(item.id); }} className="flex items-center gap-1 hover:text-red-500 transition-colors">
                <SafeIcon icon={FiHeart} className={`w-3.5 h-3.5 ${item.liked ? 'fill-red-500 text-red-500' : ''}`} />
                <span className="text-[10px] font-bold">{item.likes}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const TabShowcase = ({ memorial }) => {
  const [filter, setFilter] = useState('all');
  const [shareData, setShareData] = useState(null);
  const [isAddMemoryOpen, setIsAddMemoryOpen] = useState(false);
  const [showcaseItems, setShowcaseItems] = useState([
    { id: 1, type: 'quote', text: "Eleanor taught me that a garden is a friend you can visit anytime.", author: "Mary Peterson", likes: 24, liked: false },
    { id: 2, type: 'photo', url: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=2070&auto=format&fit=crop", author: "James Miller", likes: 42, liked: true },
    { id: 3, type: 'gif', url: "https://media.giphy.com/media/l41lI4bWfQ0aFqY6o/giphy.gif", author: "Sarah Jenkins", likes: 31, liked: false },
    { id: 4, type: 'memory', title: "The Summer of '82", text: "I'll never forget the picnic by the lake. Eleanor brought her famous apple pie.", author: "Uncle Bob", likes: 15, liked: false, url: "https://images.unsplash.com/photo-1501139083538-0139583c060f?q=80&w=2070&auto=format&fit=crop" },
    { id: 5, type: 'quote', text: "She was the glue that held this neighborhood together.", author: "City Council", likes: 56, liked: false },
  ]);

  const handleLike = (id) => {
    setShowcaseItems(prev => prev.map(item =>
      item.id === id ? { ...item, liked: !item.liked, likes: item.liked ? item.likes - 1 : item.likes + 1 } : item
    ));
  };

  const handleShare = (item) => {
    setShareData({
      title: `A memory of ${memorial.name}`,
      memorialId: memorial.id,
      url: `?memory=${item.id}`,
      description: item.type === 'quote' ? item.text : item.title || "A beautiful moment captured."
    });
  };

  const handleAddMemory = (newMemory) => setShowcaseItems([newMemory, ...showcaseItems]);
  const filteredItems = filter === 'all' ? showcaseItems : showcaseItems.filter(i => i.type === filter);

  return (
    <div className="space-y-8">
      <div className="bg-white p-8 md:p-12 rounded-[40px] shadow-sm border border-brand-dark flex flex-col md:flex-row items-center justify-between gap-8">
        <div>
          <h3 className="text-2xl font-serif text-accent-navy mb-2">The Memory Wall</h3>
          <p className="text-text-muted font-light">A shared collection of moments, words, and photos.</p>
        </div>
        <button onClick={() => setIsAddMemoryOpen(true)} className="flex-grow md:flex-none btn-accent px-8 py-4 flex items-center justify-center gap-3">
          <SafeIcon icon={FiPlus} /> Share Memory
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex flex-wrap items-center gap-3">
          {['all', 'photo', 'gif', 'memory', 'quote'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-6 py-2.5 rounded-full text-xs font-bold transition-all border-2 uppercase tracking-widest ${filter === f ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
            >
              {f === 'all' ? 'Everything' : f === 'gif' ? 'Animated' : `${f}s`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-6 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">
          <div className="flex items-center gap-2"><SafeIcon icon={FiSmile} className="text-accent-gold w-4 h-4" /><span>{showcaseItems.length} Contributions</span></div>
          <div className="flex items-center gap-2"><SafeIcon icon={FiCamera} className="text-accent-gold w-4 h-4" /><span>{showcaseItems.filter(i => i.type === 'photo').length} Photos</span></div>
        </div>
      </div>

      <div className="columns-1 md:columns-2 lg:columns-2 gap-6 space-y-6">
        <AnimatePresence mode="popLayout">
          {filteredItems.map((item, idx) => (
            <ShowcaseCard key={item.id} item={item} index={idx} onLike={handleLike} onShare={handleShare} />
          ))}
        </AnimatePresence>
      </div>

      <ShareModal isOpen={!!shareData} onClose={() => setShareData(null)} {...shareData} />
      <AddMemoryModal isOpen={isAddMemoryOpen} onClose={() => setIsAddMemoryOpen(false)} onSubmit={handleAddMemory} memorial={memorial} />
    </div>
  );
};

export default TabShowcase;